import * as React from "react";
import { Button, Chip, Input, Label, Segmented, cn } from "@moanhot/ui";
import { useAdminApi, type AdminTenant, type AdminTenantDetail, type TenantSort } from "@/api/adminClient";
import { FIELD, NUM, TD, TH, dollars, pct, when } from "@/screens/format";
import { useAdminAction, useAdminLoad } from "@/screens/hooks";
import { EmptyNote, LoadState, Page, Problem, Table } from "@/screens/ui";

const STATUS_TONE: Record<AdminTenant["status"], string> = {
  active: "text-ok",
  pending: "text-ink-2",
  suspended: "text-err",
  rejected: "text-ink-3",
};

const FILTERS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "suspended", label: "Suspended" },
];

const SORTS: { key: TenantSort; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "balance", label: "Balance" },
  { key: "spend", label: "Spend 30 d" },
  { key: "margin", label: "Margin 30 d" }, // lowest first: the ones to look at
];

function StatusForm({ tenant, onDone }: { tenant: AdminTenantDetail; onDone: (t: AdminTenantDetail, message: string) => void }) {
  const api = useAdminApi();
  const { busy, problem, setProblem, run } = useAdminAction();
  const [reason, setReason] = React.useState("");
  const suspending = tenant.status === "active";
  if (tenant.status !== "active" && tenant.status !== "suspended") return null;
  const id = `status-reason-${tenant.id}`;
  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (reason.trim().length < 3) return setProblem("Give a reason. It is kept in the audit log.");
        const call = suspending ? api.suspendTenant(tenant.id, reason.trim()) : api.reinstateTenant(tenant.id, reason.trim());
        run(call, (t) => {
          setReason("");
          onDone(t, suspending ? `${t.name} suspended. Its API calls are refused from now on.` : `${t.name} reinstated.`);
        });
      }}
    >
      <Label htmlFor={id}>{suspending ? "Reason to suspend" : "Reason to reinstate"}</Label>
      <textarea id={id} rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className={FIELD} />
      <Problem text={problem} />
      <Button type="submit" variant={suspending ? "secondary" : "primary"} disabled={busy}>
        {suspending ? "Suspend tenant" : "Reinstate tenant"}
      </Button>
    </form>
  );
}

function LimitsForm({ tenant, onDone }: { tenant: AdminTenantDetail; onDone: (t: AdminTenantDetail, message: string) => void }) {
  const api = useAdminApi();
  const { busy, problem, setProblem, run } = useAdminAction();
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        const data = new FormData(e.currentTarget);
        const rpm = Number(data.get("rpm"));
        const cap = Number(data.get("cap"));
        const screens = Number(data.get("screens"));
        const reason = String(data.get("reason") ?? "").trim();
        if ([rpm, cap, screens].some((n) => !Number.isInteger(n) || n < 1)) return setProblem("Limits are whole numbers, 1 or more.");
        if (reason.length < 3) return setProblem("Give a reason. It is kept in the audit log.");
        run(api.setTenantLimits(tenant.id, { requests_per_minute: rpm, queue_cap: cap, screens_per_minute: screens, reason }), (t) => onDone(t, `Limits saved for ${t.name}.`));
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="lim-rpm">Requests per minute</Label>
          <Input id="lim-rpm" name="rpm" inputMode="numeric" defaultValue={tenant.limits.requests_per_minute} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lim-cap">Queue cap</Label>
          <Input id="lim-cap" name="cap" inputMode="numeric" defaultValue={tenant.limits.queue_cap} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lim-screens">Screens per minute</Label>
          <Input id="lim-screens" name="screens" inputMode="numeric" defaultValue={tenant.limits.screens_per_minute} className="font-mono" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="lim-reason">Reason</Label>
        <Input id="lim-reason" name="reason" maxLength={500} />
      </div>
      <Problem text={problem} />
      <Button type="submit" variant="secondary" disabled={busy}>
        Save limits
      </Button>
    </form>
  );
}

/** Basis points as a percentage of the list price: 15000 -> "150%". */
function rateLabel(bp: number): string {
  return `${bp / 100}% of list`;
}

function RateForm({ tenant, onDone }: { tenant: AdminTenantDetail; onDone: (t: AdminTenantDetail, message: string) => void }) {
  const api = useAdminApi();
  const { busy, problem, setProblem, run } = useAdminAction();
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        const data = new FormData(e.currentTarget);
        const raw = String(data.get("rate") ?? "").trim();
        if (raw === "") return setProblem("Enter a rate. 100 is the list price.");
        const percent = Number(raw);
        const reason = String(data.get("reason") ?? "").trim();
        const bp = Math.round(percent * 100);
        if (!Number.isFinite(percent) || percent < 0 || percent > 1000 || Math.abs(percent * 100 - bp) > 1e-6)
          return setProblem("The rate is a percentage from 0 to 1000, at most two decimals.");
        if (reason.length < 3) return setProblem("Give a reason. It is kept in the audit log.");
        if (bp === 0 && data.get("confirm_free") !== "on") return setProblem("A 0% rate makes this tenant free. Tick the box to confirm.");
        run(api.setTenantPriceMultiplier(tenant.id, { price_multiplier_bp: bp, reason }), (t) =>
          onDone(t, `${t.name} now pays ${rateLabel(t.price_multiplier_bp)}.`),
        );
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="rate-pct">Rate, % of list price</Label>
        <Input id="rate-pct" name="rate" inputMode="decimal" defaultValue={tenant.price_multiplier_bp / 100} className="font-mono" />
        <p className="text-xs text-ink-3">0 is free. Owner admins only. The tenant gets price.changed at once.</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="rate-reason">Reason for the rate</Label>
        <Input id="rate-reason" name="reason" maxLength={500} />
      </div>
      <div className="flex items-center gap-2 text-sm text-ink-2">
        <input id="rate-free" type="checkbox" name="confirm_free" />
        <label htmlFor="rate-free">Yes, this tenant pays nothing (only for a 0% rate)</label>
      </div>
      <Problem text={problem} />
      <Button type="submit" variant="secondary" disabled={busy}>
        Save rate
      </Button>
    </form>
  );
}

function Detail({ id, onChanged }: { id: string; onChanged: () => void }) {
  const [load, reload] = useAdminLoad((api) => api.getTenant(id), [id], "Could not load this tenant.");
  const [notice, setNotice] = React.useState<string | null>(null);
  const done = (_t: AdminTenantDetail, message: string) => {
    setNotice(message);
    reload();
    onChanged();
  };
  return (
    <section aria-label="Tenant detail" className="rounded-lg border border-line p-5">
      <LoadState load={load}>
        {(t) => (
          <div className="space-y-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-md font-medium">{t.name}</h2>
              <span className="font-mono text-xs text-ink-3">{t.id}</span>
            </div>
            {notice && (
              <p role="status" className="text-sm text-ink-2">
                {notice}
              </p>
            )}
            <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
              <dt className="text-ink-3">Status</dt>
              <dd>
                <Chip className={STATUS_TONE[t.status]}>{t.status}</Chip>
                {t.status_reason && <span className="ml-2 text-ink-2">{t.status_reason}</span>}
              </dd>
              <dt className="text-ink-3">Balance</dt>
              <dd className="font-mono tabular-nums">
                {dollars(t.money.balance_micro_usd)} <span className="font-sans text-xs text-ink-3">held {dollars(t.money.held_micro_usd)}</span>
              </dd>
              <dt className="text-ink-3">Keys</dt>
              <dd>
                {t.keys.live_active} live, {t.keys.test_active} test, {t.keys.revoked_or_expired} revoked or expired. Last used {when(t.keys.last_used_at)}.
              </dd>
              <dt className="text-ink-3">Limits</dt>
              <dd className="font-mono tabular-nums">
                {t.limits.requests_per_minute} rpm, {t.limits.screens_per_minute} screens a minute, queue {t.limits.queue_cap}
              </dd>
              <dt className="text-ink-3">Price</dt>
              <dd className="font-mono tabular-nums">{rateLabel(t.price_multiplier_bp)}</dd>
              <dt className="text-ink-3">Webhooks</dt>
              <dd>{t.webhook_endpoints} endpoints</dd>
              <dt className="text-ink-3">RunPod</dt>
              <dd>{t.runpod_status === "none" ? "No account" : t.runpod_status}</dd>
              <dt className="text-ink-3">Activated</dt>
              <dd>{when(t.activated_at)}</dd>
            </dl>
            <div className="grid gap-6 border-t border-line pt-5 lg:grid-cols-2">
              <StatusForm key={`s-${t.status}`} tenant={t} onDone={done} />
              <LimitsForm key={`l-${t.limits.requests_per_minute}-${t.limits.queue_cap}-${t.limits.screens_per_minute}`} tenant={t} onDone={done} />
              <RateForm key={`r-${t.price_multiplier_bp}`} tenant={t} onDone={done} />
            </div>
          </div>
        )}
      </LoadState>
    </section>
  );
}

/** Every tenant with its money: balance, 30-day spend, GPU cost and margin. */
export function Tenants() {
  const [status, setStatus] = React.useState("all");
  const [sort, setSort] = React.useState<TenantSort>("name");
  const [open, setOpen] = React.useState<string | null>(null);
  const [load, reload] = useAdminLoad((api) => api.listTenants(status === "all" ? null : status, sort), [status, sort], "Could not load tenants.");

  return (
    <Page title="Tenants" actions={<Segmented value={status} onChange={setStatus} options={FILTERS} label="Status" size="sm" />}>
      <LoadState load={load} empty={(d) => (d.data.length === 0 ? <EmptyNote title="No tenants" body="No tenant has this status." /> : null)}>
        {(d) => (
          <Table
            label="Tenants"
            head={
              <>
                {SORTS.map((s, i) => (
                  <th key={s.key} className={cn(TH, i > 0 && "text-right")} aria-sort={sort === s.key ? (s.key === "name" || s.key === "margin" ? "ascending" : "descending") : undefined}>
                    <button type="button" onClick={() => setSort(s.key)} className={cn("hover:text-ink", sort === s.key && "text-ink")}>
                      {s.label}
                    </button>
                  </th>
                ))}
                <th className={cn(TH, "text-right")}>GPU cost 30 d</th>
                <th className={cn(TH, "text-right")}>Running</th>
                <th className={TH}>Status</th>
              </>
            }
          >
            {d.data.map((t) => (
              <tr key={t.id} className={cn("cursor-pointer hover:bg-hover", open === t.id && "bg-selected")} onClick={() => setOpen(open === t.id ? null : t.id)}>
                <td className={TD}>
                  <button type="button" className="text-left font-medium" aria-expanded={open === t.id}>
                    {t.name}
                  </button>
                  <div className="font-mono text-xs text-ink-3">{t.id}</div>
                </td>
                <td className={NUM}>{dollars(t.money.balance_micro_usd)}</td>
                <td className={NUM}>{dollars(t.money.spend_30d_micro_usd)}</td>
                <td className={cn(NUM, t.money.margin_low && "text-err")}>
                  {dollars(t.money.margin_30d_micro_usd)}
                  <div className="text-xs">{pct(t.money.margin_30d_pct)}</div>
                </td>
                <td className={NUM}>{dollars(t.money.gpu_cost_30d_micro_usd)}</td>
                <td className={NUM}>{t.running_jobs}</td>
                <td className={TD}>
                  <Chip className={STATUS_TONE[t.status]}>{t.status}</Chip>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </LoadState>
      {open && (
        <div className="mt-6">
          <Detail id={open} onChanged={reload} />
        </div>
      )}
    </Page>
  );
}
